/**
 * Price data.
 *
 * Backed by DefiLlama (`coins.llama.fi`) — keyless, free, and unlimited in
 * history, which CoinGecko's public API is not: it refuses anything older than
 * 365 days, and every trade in this database predates that window.
 *
 * DefiLlama addresses coins as `coingecko:bitcoin`, the exact string already
 * stored in `Trade.coinBase`, so no id mapping is needed.
 *
 * All DefiLlama prices are USD. Non-USD quotes are derived by dividing two USD
 * prices from a single batched response, which was measured at 0.034% against
 * Binance's live ETH/BTC pair. That replaces CoinGecko's one-request-per-quote
 * pattern with one request in total.
 *
 * Everything sits behind {@link PriceSource} so Binance or Coinbase — both of
 * which also serve deep history without a key — can be substituted if
 * DefiLlama changes. It is a public good with no SLA and no rate-limit
 * headers, so batch and cache rather than poll.
 */
import type { TradePair } from "@/domain/types";
import { getFiatPerUsd } from "./metadata";

const DEFILLAMA = "https://coins.llama.fi";

export interface PricePoint {
  /** Price in USD. */
  usd: number;
  /** When this price was observed — DefiLlama snaps to the nearest datapoint. */
  at: Date;
  symbol: string;
  /** DefiLlama's own 0–1 confidence in the figure. Surfaced, not discarded. */
  confidence?: number;
}

export interface SeriesPoint {
  at: Date;
  usd: number;
}

export interface PriceSource {
  currentPrices(coinIds: readonly string[]): Promise<Map<string, PricePoint>>;
  historicalPrices(coinIds: readonly string[], at: Date): Promise<Map<string, PricePoint>>;
  priceSeries(coinId: string, from: Date, points?: number): Promise<SeriesPoint[]>;
}

export class PriceLookupError extends Error {
  constructor(
    message: string,
    readonly cause?: unknown,
  ) {
    super(message);
    this.name = "PriceLookupError";
  }
}

/** `bitcoin` -> `coingecko:bitcoin`, leaving already-qualified ids alone. */
function qualify(coinId: string): string {
  return coinId.includes(":") ? coinId : `coingecko:${coinId}`;
}

function unqualify(key: string): string {
  return key.startsWith("coingecko:") ? key.slice("coingecko:".length) : key;
}

interface LlamaCoin {
  price?: number;
  symbol?: string;
  timestamp?: number;
  confidence?: number;
}

async function llama<T>(path: string): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${DEFILLAMA}${path}`);
  } catch (cause) {
    throw new PriceLookupError(`Network error reaching DefiLlama (${path})`, cause);
  }
  if (!response.ok) {
    throw new PriceLookupError(`DefiLlama returned ${response.status} for ${path}`);
  }
  return (await response.json()) as T;
}

function toPricePoints(coins: Record<string, LlamaCoin>): Map<string, PricePoint> {
  const out = new Map<string, PricePoint>();

  for (const [key, coin] of Object.entries(coins)) {
    if (typeof coin.price !== "number" || !Number.isFinite(coin.price)) continue;
    out.set(unqualify(key), {
      usd: coin.price,
      at: new Date((coin.timestamp ?? 0) * 1000),
      symbol: coin.symbol ?? "",
      confidence: coin.confidence,
    });
  }

  return out;
}

export const defiLlama: PriceSource = {
  async currentPrices(coinIds) {
    if (coinIds.length === 0) return new Map();
    const list = [...new Set(coinIds)].map(qualify).join(",");
    const body = await llama<{ coins: Record<string, LlamaCoin> }>(
      `/prices/current/${encodeURIComponent(list)}`,
    );
    return toPricePoints(body.coins ?? {});
  },

  async historicalPrices(coinIds, at) {
    if (coinIds.length === 0) return new Map();
    const list = [...new Set(coinIds)].map(qualify).join(",");
    const seconds = Math.floor(at.getTime() / 1000);
    const body = await llama<{ coins: Record<string, LlamaCoin> }>(
      `/prices/historical/${seconds}/${encodeURIComponent(list)}`,
    );
    return toPricePoints(body.coins ?? {});
  },

  async priceSeries(coinId, from, points = 120) {
    const seconds = Math.floor(from.getTime() / 1000);
    const body = await llama<{
      coins: Record<string, { prices?: { timestamp: number; price: number }[] }>;
    }>(`/chart/${encodeURIComponent(qualify(coinId))}?start=${seconds}&span=${points}&period=1d`);

    const series = Object.values(body.coins ?? {})[0]?.prices ?? [];
    return series.map((p) => ({ at: new Date(p.timestamp * 1000), usd: p.price }));
  },
};

/**
 * How a trade's quote currency converts to USD.
 *
 * Stablecoins are resolved as coins rather than assumed to equal $1 — USDC was
 * trading at 0.9998 when this was written, and the app reports profit to more
 * precision than that.
 */
type QuoteResolution =
  | { kind: "usd" }
  | { kind: "coin"; coinId: string }
  | { kind: "fiat"; code: string };

const QUOTE_CURRENCIES: Record<string, QuoteResolution> = {
  USD: { kind: "usd" },
  USDT: { kind: "coin", coinId: "tether" },
  USDC: { kind: "coin", coinId: "usd-coin" },
  DAI: { kind: "coin", coinId: "dai" },
  BUSD: { kind: "coin", coinId: "binance-usd" },
  BTC: { kind: "coin", coinId: "bitcoin" },
  ETH: { kind: "coin", coinId: "ethereum" },
  BNB: { kind: "coin", coinId: "binancecoin" },
  EUR: { kind: "fiat", code: "eur" },
  GBP: { kind: "fiat", code: "gbp" },
  JPY: { kind: "fiat", code: "jpy" },
  CAD: { kind: "fiat", code: "cad" },
};

export function resolveQuote(coinTarget: string): QuoteResolution {
  return QUOTE_CURRENCIES[coinTarget.toUpperCase()] ?? { kind: "usd" };
}

/** Coin ids that must be fetched to price every pair in `pairs`. */
export function coinIdsForPairs(pairs: readonly TradePair[]): string[] {
  const ids = new Set<string>();

  for (const pair of pairs) {
    ids.add(pair.coinBase);
    const quote = resolveQuote(pair.coinTarget);
    if (quote.kind === "coin") ids.add(quote.coinId);
  }

  return [...ids];
}

/**
 * Convert a USD price into a trade's quote currency.
 *
 * Returns null when the quote cannot be resolved — a missing rate is not the
 * same as a price of zero, and the 2020 code's habit of collapsing the two is
 * what produced NaN totals downstream.
 */
export function toQuoteCurrency(
  usdPrice: number,
  coinTarget: string,
  usdPrices: ReadonlyMap<string, PricePoint>,
  fiatPerUsd?: ReadonlyMap<string, number>,
): number | null {
  const quote = resolveQuote(coinTarget);

  switch (quote.kind) {
    case "usd":
      return usdPrice;

    case "coin": {
      const quotePrice = usdPrices.get(quote.coinId)?.usd;
      if (!quotePrice) return null;
      return usdPrice / quotePrice;
    }

    case "fiat": {
      const rate = fiatPerUsd?.get(quote.code);
      if (!rate) return null;
      return usdPrice * rate;
    }
  }
}

/** Current price of every pair, expressed in each pair's own quote currency. */
export async function getPairPrices(
  pairs: readonly TradePair[],
  source: PriceSource = defiLlama,
): Promise<Map<string, number>> {
  if (pairs.length === 0) return new Map();

  const usdPrices = await source.currentPrices(coinIdsForPairs(pairs));
  const needsFiat = pairs.some((p) => resolveQuote(p.coinTarget).kind === "fiat");
  const fiatPerUsd = needsFiat ? await getFiatPerUsd() : undefined;

  const out = new Map<string, number>();
  for (const pair of pairs) {
    const base = usdPrices.get(pair.coinBase)?.usd;
    if (base == null) continue;

    const converted = toQuoteCurrency(base, pair.coinTarget, usdPrices, fiatPerUsd);
    if (converted != null) out.set(`${pair.coinBase}/${pair.coinTarget}`, converted);
  }

  return out;
}

/** Price of one pair on a given date — the lookup CoinGecko no longer serves. */
export async function getPairPriceOn(
  pair: TradePair,
  at: Date,
  source: PriceSource = defiLlama,
): Promise<number | null> {
  const usdPrices = await source.historicalPrices(coinIdsForPairs([pair]), at);
  const base = usdPrices.get(pair.coinBase)?.usd;
  if (base == null) return null;

  const quote = resolveQuote(pair.coinTarget);
  // Historical fiat rates are not available free; fall back to current rates.
  const fiatPerUsd = quote.kind === "fiat" ? await getFiatPerUsd() : undefined;

  return toQuoteCurrency(base, pair.coinTarget, usdPrices, fiatPerUsd);
}
